import * as Api from '@ViewModels';
import React, { useMemo } from 'react';
import { useStreamingResponseMutation } from '../../../../hooks/useStreamingResponseMutation';
import { IContentCalendarSelectedSuggestion } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { getUniqueIdForSuggestion } from '../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';

type ChatRequestTypeAction = {
	_type: 'ContentGenerationAction';
	contentType: 'email' | 'social' | 'blogTemplate';
} & ({ contentId: string } | { content: any });

type ChatRequestType = {
	_type: 'AIActionRequest';
	action: ChatRequestTypeAction;
};

export const useHandleAiGeneration = <TToolCall extends Api.EmailToolCall | Api.SocialMediaToolCall>({
	onAiGenerationComplete,
	impersonationContext,
	contentType,
	customContent,
}: {
	onAiGenerationComplete: (
		suggestion: IContentCalendarSelectedSuggestion,
		toolCall: TToolCall,
		conversationId: string
	) => void;
	impersonationContext: Api.IImpersonationContext;
	contentType: 'email' | 'social' | 'blogTemplate';
	customContent?: Record<string, { subject: string; content: Api.IRawRichTextContentState }>;
}) => {
	const userSession = useUserSession<AdminUserSessionContext>();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useHandleAiGeneration');

	// Refs to track currently processing item and generated content (fixes race conditions)
	const currentlyProcessingRef = React.useRef<IContentCalendarSelectedSuggestion | null>(null);
	const currentGeneratedContentRef = React.useRef<{
		suggestion: IContentCalendarSelectedSuggestion;
		toolCall: TToolCall;
		conversationId: string;
	} | null>(null);

	const [pendingItems, setPendingItems] = React.useState<IContentCalendarSelectedSuggestion[]>([]);
	const [isCurrentlyProcessing, setIsCurrentlyProcessing] = React.useState(false);
	const isGenerating = useMemo(
		() => pendingItems.length > 0 || isCurrentlyProcessing,
		[pendingItems, isCurrentlyProcessing]
	);

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('useHandleAiGeneration', error);
		},
	});

	const [suggestionsWithErrors, setSuggestionsWithErrors] = React.useState<IContentCalendarSelectedSuggestion[]>([]);

	const handleAiGenerationComplete = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion, toolCall: TToolCall, conversationId: string) => {
			onAiGenerationComplete(suggestion, toolCall, conversationId);
		},
		[onAiGenerationComplete]
	);

	// Use refs to avoid circular dependencies
	const processItemRef = React.useRef<((suggestion: IContentCalendarSelectedSuggestion) => Promise<void>) | null>(null);
	const processNextItemRef = React.useRef<(() => void) | null>(null);

	const onErrorRef = React.useRef<((error: Error, suggestion: IContentCalendarSelectedSuggestion) => void) | null>(
		null
	);

	const onError = React.useCallback(
		(error: Error, suggestion: IContentCalendarSelectedSuggestion) => {
			errorMessages.pushApiError(error);
			setSuggestionsWithErrors(prev => [...prev, suggestion]);

			// Clear refs and state
			currentlyProcessingRef.current = null;
			currentGeneratedContentRef.current = null;
			setIsCurrentlyProcessing(false);

			setPendingItems(prev => {
				const filtered = prev.filter(item => getUniqueIdForSuggestion(item) !== getUniqueIdForSuggestion(suggestion));
				// Process next item if queue is not empty
				if (filtered.length > 0 && processNextItemRef.current) {
					// Use setTimeout to ensure state update completes before processing next
					setTimeout(() => processNextItemRef.current?.(), 0);
				}
				return filtered;
			});

			logger.logApiError('useHandleAiGeneration', error as Api.IOperationResultNoValue);
		},
		[errorMessages, logger]
	);

	onErrorRef.current = onError;

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			conversationId,
			chatRequest,
		}: {
			assistantName: string;
			conversationId: string;
			suggestion: IContentCalendarSelectedSuggestion;
			chatRequest: ChatRequestType;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${conversationId}/stream`,
						impersonationContext,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, params) => {
			const data = JSON.parse(event.data);
			if (event.event === 'Error') {
				if (onErrorRef.current) {
					onErrorRef.current(new Error(event.data), params.suggestion);
				}
				return;
			}

			if (event.event !== 'ToolResponse') {
				return;
			}

			const toolCall = data?.toolCall;

			if (
				toolCall?.body ||
				(toolCall?.toolName === 'lev-social-media-post' && toolCall?.content) ||
				(toolCall?.toolName === 'lev-blog' && toolCall?.content)
			) {
				// Store in ref to avoid race condition with onSuccess
				currentGeneratedContentRef.current = {
					suggestion: params.suggestion,
					toolCall,
					conversationId: params.conversationId,
				};
			}
		},
		onSuccess: params => {
			// Read from ref instead of state to avoid race condition
			const generatedContent = currentGeneratedContentRef.current;

			// Clear refs and state first
			currentlyProcessingRef.current = null;
			currentGeneratedContentRef.current = null;
			setIsCurrentlyProcessing(false);

			if (generatedContent) {
				handleAiGenerationComplete(
					generatedContent.suggestion,
					generatedContent.toolCall,
					generatedContent.conversationId
				);
			} else {
				setSuggestionsWithErrors(prev => [...prev, params.suggestion]);
			}

			// Remove completed item and process next if queue is not empty
			setPendingItems(prev => {
				const filtered = prev.filter(
					item => getUniqueIdForSuggestion(item) !== getUniqueIdForSuggestion(params.suggestion)
				);
				// Process next item if queue is not empty
				if (filtered.length > 0 && processNextItemRef.current) {
					// Use setTimeout to ensure state update completes before processing next
					setTimeout(() => processNextItemRef.current?.(), 0);
				}
				return filtered;
			});
		},
		onError: (error, params) => {
			if (onErrorRef.current) {
				onErrorRef.current(error, params.suggestion);
			}
		},
	});

	const processItem = React.useCallback(
		async (suggestion: IContentCalendarSelectedSuggestion) => {
			// Set currently processing ref FIRST (before any async operations)
			// This prevents race conditions where multiple items try to process simultaneously
			currentlyProcessingRef.current = suggestion;
			currentGeneratedContentRef.current = null;
			setIsCurrentlyProcessing(true);

			try {
				const result = await aiAssistantCreateConversationMutation.mutateAsync({
					assistantName: 'ContentGenerationAgent',
					impersonationContext,
				});

				const customContentForSuggestion = customContent?.[getUniqueIdForSuggestion(suggestion)];

				const action: any = {
					_type: 'ContentGenerationAction',
					contentType,
				} as any;

				if (customContentForSuggestion) {
					action.content = customContentForSuggestion.content;
				} else {
					action.contentId = suggestion.templateReference.templateId;
				}

				// Use aiPromptMutation directly from closure - it's always the latest version
				aiPromptMutation.mutate({
					assistantName: 'ContentGenerationAgent',
					conversationId: result.id,
					suggestion,
					chatRequest: {
						_type: 'AIActionRequest',
						action,
					},
				});
			} catch (error) {
				// If conversation creation fails, clear refs and handle error
				currentlyProcessingRef.current = null;
				currentGeneratedContentRef.current = null;
				setIsCurrentlyProcessing(false);
				if (onErrorRef.current) {
					onErrorRef.current(error as Error, suggestion);
				}
			}
		},
		[aiAssistantCreateConversationMutation, aiPromptMutation, impersonationContext, contentType, customContent]
	);

	processItemRef.current = processItem;

	// Helper function to process next item in queue
	const processNextItem = React.useCallback(() => {
		// Check if already processing BEFORE reading state
		if (currentlyProcessingRef.current !== null) {
			return;
		}
		setPendingItems(prev => {
			// Double-check inside state update to prevent race conditions
			if (prev.length === 0 || currentlyProcessingRef.current !== null) {
				return prev;
			}
			const nextItem = prev[0];
			// Call processItem which will set the ref internally
			if (processItemRef.current) {
				processItemRef.current(nextItem);
			}
			return prev;
		});
	}, []);

	processNextItemRef.current = processNextItem;

	const queueItemForAiGeneration = (suggestion: IContentCalendarSelectedSuggestion) => {
		setSuggestionsWithErrors(prev => {
			return prev.filter(item => getUniqueIdForSuggestion(item) !== getUniqueIdForSuggestion(suggestion));
		});
		setPendingItems(prev => {
			const newItems = [...prev, suggestion];
			return newItems;
		});
	};

	const processQueue = React.useCallback(() => {
		// Use setTimeout to ensure any pending state updates from queueItemForAiGeneration complete first
		setTimeout(() => {
			if (processNextItemRef.current) {
				processNextItemRef.current();
			}
		}, 0);
	}, []);

	const clearQueue = React.useCallback(() => {
		setPendingItems([]);
		currentlyProcessingRef.current = null;
		currentGeneratedContentRef.current = null;
		setIsCurrentlyProcessing(false);
	}, []);

	return {
		pendingItems,
		queueItemForAiGeneration,
		isGenerating,
		processQueue,
		clearQueue,
		suggestionsWithErrors,
	};
};
